import React, { useState, useEffect } from "react";
import { Backdrop, CircularProgress, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { RadioButton } from "primereact/radiobutton";
import { TabView, TabPanel } from "primereact/tabview";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import "./table.css";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import Select from "react-select";
import dayjs from "dayjs";
import { Dropdown } from "primereact/dropdown";
import SurveyThankYou from "./SurveyThankYou";

/** Max 100 for "% of total organizational emissions" when scope 1&2 / scope 3 is Yes. */
const clampOrgEmissionsPercentInput = (raw) => {
  if (raw === "") return "";
  if (!/^\d+(\.\d{0,})?$/.test(raw)) return raw;
  const n = parseFloat(raw);
  if (Number.isNaN(n)) return raw;
  if (n > 100) return "100";
  return raw;
};

const SCFScope = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0); // Track the active tab (0: Scope 1&2, 1: Scope 3)
  const [formData, setFormData] = useState({
    scope1And2Calculation: "",
    scope3Calculation: "",
    currency1: "",
    currency2: "",
    currency3: "",
    currency4: "",
  });
  const [errors, setErrors] = useState({});
  const [surveyFromDate, setSurveyFromDate] = useState("");
  const [surveyToDate, setSurveyToDate] = useState("");
  const [validFromDate, setValidFromDate] = useState("");
  const [validToDate, setValidToDate] = useState("");
  const [surveyToken, setSurveyToken] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [emissionFactor, setEmissionFactor] = useState("");
  const [rowData, setRowData] = useState([]);
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
  };
  const [currency, setCurrency] = useState([]);

  const showSurveySubmittedState = () => {
    const storedScfStep1 = localStorage.getItem("scfStep1");
    const parsedScfStep1 = storedScfStep1 ? JSON.parse(storedScfStep1) : null;
    const tokenToUse = parsedScfStep1?.token || surveyToken;
    if (tokenToUse) {
      localStorage.setItem(`scf_survey_submitted_${tokenToUse}`, "true");
    }
    localStorage.removeItem("scfStep1");
    localStorage.removeItem("scfStep2");
    localStorage.removeItem("scfFormInProgress");
    setSubmitted(true);
  };

  const isDuplicateSurveyResponse = (responsePayload) => {
    const normalized = JSON.stringify(responsePayload || {}).toLowerCase();
    return (
      normalized.includes("duplicate survey") ||
      normalized.includes("already submitted") ||
      normalized.includes("already filled")
    );
  };

  // Fetch currency data from API
  async function fetchCurrency() {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_API_URL}currencies`,
        {
          method: "GET",
          headers: {
            "Content-type": "application/json; charset=UTF-8",
            username: localStorage.getItem("userName"),
          },
        }
      );
      const data = await response.json();
      const formattedCurrency = data.map((currency) => ({
        label: currency,
        value: currency,
      }));
      setCurrency(formattedCurrency);
    } catch (error) {
      console.error("Error fetching currency data:", error);
    }
  }

  useEffect(() => {
    // const visited = sessionStorage.getItem("surveyStarted");
    // if (!visited) {
    //   localStorage.removeItem('scfStep1');
    //   localStorage.removeItem('scfStep2');
    //   sessionStorage.setItem("surveyStarted", "true");
    //   console.log("Storage cleared (first entry)");
    // }
    fetchCurrency();
    if (location?.state?.showThankYou === true) {
      showSurveySubmittedState();
      return;
    }
    const storedScfStep1 = localStorage.getItem("scfStep1");
    const parsedScfStep1 = JSON.parse(storedScfStep1);
    const storedScfStep2 = localStorage.getItem("scfStep2");
    const parsedScfStep2 = JSON.parse(storedScfStep2);
    if (!parsedScfStep1 || !parsedScfStep2) {
      setSubmitted(true);
      return;
    }
    setEmissionFactor(parsedScfStep2.emissionFactor);
    const tokenFromUrl = parsedScfStep1.token;
    const empEmail = parsedScfStep1.email;
    setEmail(empEmail);
    if (tokenFromUrl) {
      console.log(tokenFromUrl);
      const decodedString = atob(tokenFromUrl);
      const [
        companyid,
        formattedFromDate,
        formattedToDate,
        formattedExpiryDate,
        formattedDate,
        supplierName,
      ] = decodedString.split(":");
      console.log("Decoded String:", decodedString);

      const fromParts = formattedFromDate.split("-");
      const fromDate = `${fromParts[2]}-${fromParts[1]}-${fromParts[0]}`;

      const toParts = formattedToDate.split("-");
      const toDate = `${toParts[2]}-${toParts[1]}-${toParts[0]}`;
      console.log("From Date"+fromDate);
      console.log("To Date"+toDate);
      console.log("Formatted From Date"+formattedFromDate);
      console.log("Formatted To Date"+formattedToDate);
      setFormData({
        ...formData,
        surveyPeriod: fromDate + " to " + toDate,
        employeeEmail: empEmail, // Set the state based on the selected location
      });
      setCountry(parsedScfStep1.country);
      setRowData(parsedScfStep1.rowData);
      setSurveyFromDate(dayjs(formattedFromDate).format("YYYY-MM-DD"));
      setSurveyToDate(dayjs(formattedToDate).format("YYYY-MM-DD"));
      setValidFromDate(formattedFromDate);
      setValidToDate(formattedToDate);
      setSurveyToken(tokenFromUrl);
      setSupplierName(supplierName);
      setCompanyId(companyid);
      setToken(tokenFromUrl);
    }
    console.log(formData);
  }, []);

  // Function to validate the form based on the active tab
  const validateForm = () => {
    const newErrors = {};
    if (activeIndex === 0 && !formData.scope1And2Calculation) {
      newErrors.scope1And2Calculation =
        "Please select if you calculate scope 1&2 emissions.";
    } else if (activeIndex === 1 && !formData.scope3Calculation) {
      newErrors.scope3Calculation =
        "Please select if you calculate scope 3 emissions.";
    }

    // Add validation for currency1 when scope1Intensity is not empty
    if (formData.scope1Intensity && !formData.currency1) {
      newErrors.currency1 = "Please select a currency";
    }

    // Add validation for currency2 when scope2Intensity is not empty
    if (formData.scope2Intensity && !formData.currency2) {
      newErrors.currency2 = "Please select a currency";
    }

    // Add validation for currency3 when upstreamIntensity is not empty
    if (formData.upstreamIntensity && !formData.currency3) {
      newErrors.currency3 = "Please select a currency";
    }

    // Add validation for currency4 when waterIntensity is not empty
    if (formData.waterIntensity && !formData.currency4) {
      newErrors.currency4 = "Please select a currency";
    }

    if (
      activeIndex === 0 &&
      formData.scope1And2Calculation === "true" &&
      formData.scope12Percentage !== "" &&
      formData.scope12Percentage != null
    ) {
      const p = parseFloat(String(formData.scope12Percentage));
      if (!Number.isNaN(p) && p > 100) {
        newErrors.scope12Percentage = "Percentage cannot be greater than 100.";
      }
    }

    if (
      activeIndex === 1 &&
      formData.scope3Calculation === "true" &&
      formData.scope3Percentage !== "" &&
      formData.scope3Percentage != null
    ) {
      const p = parseFloat(String(formData.scope3Percentage));
      if (!Number.isNaN(p) && p > 100) {
        newErrors.scope3Percentage = "Percentage cannot be greater than 100.";
      }
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleNext = () => {
    const newErrors = validateForm();

    // Handle validation for Scope 1&2
    if (activeIndex === 0 && Object.keys(newErrors).length === 0) {
      setActiveIndex(1); // Go to Scope 3 tab if no errors for Scope 1&2
    }
    // Handle validation for Scope 3
    else if (activeIndex === 1 && Object.keys(newErrors).length === 0) {
      console.log(formData);
      handleSave();
      //navigate("/supplier-carbon-footprint"); // Redirect to the next page
    }
  };

  const handleSave = async () => {
    console.log(formData);
    console.log(validToDate);
    const fromDate=validFromDate;
    const toDate=validToDate;
    console.log("From Date"+fromDate);
    console.log("To Date"+toDate);
    setLoading(true);
    try {
    let validate = await fetch(
      `${process.env.REACT_APP_BASE_API_URL}validate-carbon-footprint-survey`,
      {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        body: JSON.stringify({
          companyId: companyId,
          token: surveyToken,
          surveyFromDate: fromDate,
          surveyToDate: toDate,
        }),
      }
    );

    if (validate.status === 200) {
      const validateText = await validate.text();
      const responseJson = validateText ? JSON.parse(validateText) : {};
      if (responseJson.success === false) {
        if (isDuplicateSurveyResponse(responseJson)) {
          setLoading(false);
          showSurveySubmittedState();
          return;
        }
        setLoading(false);
        console.log(responseJson);
        Swal.fire({
                      width: 400,
                      heightAuto: false,
          icon: "error",
          html: `<h4 style="color:#CC2229; font-size: 20px; font-weight: bold;">Error!</h4><p style="font-size: 16px;">${responseJson.data}</p>`,
          iconHtml:
            '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
        });
      } else if (responseJson.status === 400) {
        setLoading(false);
        Swal.fire({
          icon: "error",
          html: `<h4 style="color:#CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
              <p style="font-size: 16px;">${responseJson.title}</p>`,
          iconHtml:
            '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
        });
      } else {
        const scope3CalculationBoolean = formData.scope3Calculation
          ? formData.scope3Calculation.toLowerCase() === "true"
          : false;
        //const scope3CalculationBoolean = formData.scope3Calculation.toLowerCase() === "true";
        const scope3VerificationBoolean = formData.scope3Verification
          ? formData.scope3Verification.toLowerCase() === "true"
          : false;
        const scope12VerificationBoolean = formData.scope12Verification
          ? formData.scope12Verification.toLowerCase() === "true"
          : false;
        const scope1And2CalculationBoolean = formData.scope1And2Calculation
          ? formData.scope1And2Calculation.toLowerCase() === "true"
          : false;
        const emissionFactorBoolean = emissionFactor
          ? emissionFactor.toLowerCase() === "true"
          : false;
        let res = await fetch(
          `${process.env.REACT_APP_BASE_API_URL}supplier-carbon-footprint-survey-form`,
          {
            method: "POST",
            headers: {
              "Content-type": "application/json; charset=UTF-8",
            },
            body: JSON.stringify({
              surveyFromDate: fromDate,
              surveyToDate: toDate,
              surveyToken: surveyToken,
              supplierName: supplierName,
              supplierLocation: country,
              productDetails: rowData,
              calculateSpecificCradleToGetEmissionFactor: emissionFactorBoolean,
              calculateYourCompanyScope1Scope2Emissions:
                scope1And2CalculationBoolean,
              scope1Scope2EmissionNosActualOrEstimated:
                formData.scope12NumbersType,
              scope1Scope2EmissionNosExternallyVerified:
                scope12VerificationBoolean,
              scope1EmissionstCO2e: formData.scope1Emissions,
              scope2EmissionstCO2e: formData.scope2Emissions,
              scope1EmissionIntensityBasedOnRevenue: formData.scope1Intensity,
              scope1Unit: formData.unit1,
              scope1Currency: formData.currency1,
              scope2EmissionIntensityBasedOnRevenue: formData.scope2Intensity,
              scope2Unit: formData.unit2,
              scope2Currency: formData.currency2,
              percentOfTotalOrganizationalEmissionsForScope1Scope2:
                formData.scope12Percentage,
              calculateYourCompanyScope3UpstreamAndWasteEmissions:
                scope3CalculationBoolean,
              scope3EmissionNosActualOrEstimated: formData.scope3NumbersType,
              scope3UpstreamAndWasteEmissionNosExternallyVerified:
                scope3VerificationBoolean,
              scope3UpstreamEmissionstCO2e: formData.upstreamEmissions,
              scope3WasteEmissionstCO2e: formData.wasteEmissions,
              upstreamEmissionIntensityBasedOnRevenue:
                formData.upstreamIntensity,
              upstreamUnit: formData.unit3,
              upstreamCurrency: formData.currency3,
              wasteEmissionIntensityBasedOnRevenue: formData.waterIntensity,
              wasteUnit: formData.unit4,
              wasteCurrency: formData.currency4,
              percentOfTotalOrganizationalEmissionsForScope3:
                formData.scope3Percentage,
              companyId: companyId,
            }),
          }
        );

        if (res.status === 200) {
          const resText = await res.text();
          const responseJson = resText ? JSON.parse(resText) : {};
          if (responseJson.success === false) {
            if (isDuplicateSurveyResponse(responseJson)) {
              setLoading(false);
              showSurveySubmittedState();
              return;
            }
            setLoading(false);
            console.log(responseJson);
            Swal.fire({
              icon: "error",
              html: `<h4 style="color:#CC2229; font-size: 20px; font-weight: bold;">Error!</h4><p style="font-size: 16px;">${responseJson.data}</p>`,
              iconHtml:
                '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
            });
          } else if (responseJson.status === 400) {
            setLoading(false);
            Swal.fire({
              icon: "error",
              html: `<h4 style="color:#CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
                  <p style="font-size: 16px;">${responseJson.title}</p>`,
              iconHtml:
                '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
            });
          } else {
            setLoading(false);
            showSurveySubmittedState();
          }
        } else {
          setLoading(false);
          Swal.fire({
            icon: "error",
            html: `<h4 style="color:#CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
            <p style="font-size: 16px;">Error in creating site location</p>`,
            iconHtml:
              '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
          });
        }
      }
    } else {
      setLoading(false);
      Swal.fire({
        icon: "error",
        html: `<h4 style="color:#CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
        <p style="font-size: 16px;">Error in saving survey</p>`,
        iconHtml:
          '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
      });
    }
    setLoading(false);
    } catch (err) {
      console.error("handleSave error:", err);
      setLoading(false);
      Swal.fire({
        icon: "error",
        html: `<h4 style="color:#CC2229; font-size: 20px; font-weight: bold;">Error!</h4><p style="font-size: 16px;">An unexpected error occurred. Please try again.</p>`,
        iconHtml: '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
      });
    }
  };

  const handleRadioChange = (e, field) => {
    const value = e.value;
    setFormData({ ...formData, [field]: value });
    // Clear the error for the field once selected
    setErrors({ ...errors, [field]: "" });
  };

  if (submitted) {
    return <SurveyThankYou />;
  }

  return (
    <div id="wrapper">
      {loading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={true}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <div id="">
        <header>
          <nav
            className="navbar navbar-expand-lg navbar-light"
            style={{ background: "#407775" }}
          >
            <div className="container-fluid">
              <a className="navbar-brand" href="#home">
                <img
                  src="assets/img/logo.png"
                  alt="Company Logo"
                  height="30"
                  className="d-inline-block align-top"
                />
              </a>
              <button
                className="navbar-toggler"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#navbarSupportedContent"
                aria-controls="navbarSupportedContent"
                aria-expanded="false"
                aria-label="Toggle navigation"
              >
                <span className="navbar-toggler-icon"></span>
              </button>
              <div
                className="collapse navbar-collapse justify-content-end"
                id="navbarSupportedContent"
              >
                <ul className="navbar-nav mb-2 mb-lg-0">
                  <li className="nav-item">
                    <a className="nav-link text-white" href="#profile">
                      <img
                        className="icon mr-3"
                        src="assets/img/User.png"
                        alt="User"
                      />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </nav>
        </header>
        <div className="container survey-form scf p-4 p-lg-5">
          <div className="row mb-4">
            <div className="col">
              <div className="form-header">
                <h1 className="title" style={{ color: "#407775!important" }}>
                  Supplier Carbon Footprint​ Survey
                </h1>
                <h2>Energy And Emissions</h2>
              </div>
            </div>
          </div>
          <div className="form-part">
            <form>
              <div className="row tabs">
                <TabView
                  activeIndex={activeIndex}
                  onTabChange={(e) => setActiveIndex(e.index)}
                >
                  <TabPanel header="Scope 1&2">
                    <div className="row gy-4">
                      <div className="col col-md-12">
                        <label
                          className="formlable"
                          htmlFor="scope1And2Calculation"
                        >
                          Do you calculate your company's scope 1 and 2
                          emissions? <span className="mark-form">*</span>
                        </label>
                        <div className="flex flex-wrap gap-3">
                          <div className="flex align-items-center">
                            <RadioButton
                              inputId="scope1And2Yes"
                              name="scope1And2Calculation"
                              value="true"
                              checked={
                                formData.scope1And2Calculation === "true"
                              }
                              onChange={(e) =>
                                handleRadioChange(e, "scope1And2Calculation")
                              }
                            />
                            <label
                              htmlFor="scope1And2Yes"
                              className="ml-2 mb-0 formlable"
                            >
                              Yes
                            </label>
                            <RadioButton
                              inputId="scope1And2No"
                              name="scope1And2Calculation"
                              value="false"
                              checked={
                                formData.scope1And2Calculation === "false"
                              }
                              onChange={(e) =>
                                handleRadioChange(e, "scope1And2Calculation")
                              }
                              className="ml-4"
                            />
                            <label
                              htmlFor="scope1And2No"
                              className="ml-2 mb-0 formlable"
                            >
                              No
                            </label>
                          </div>
                        </div>
                        {errors.scope1And2Calculation && (
                          <p className="text-danger">
                            {errors.scope1And2Calculation}
                          </p>
                        )}
                      </div>
                    </div>
                    {formData.scope1And2Calculation === "true" && (
                      <div className="scope1">
                        <div className="row mt-4">
                          <div className="col-md-5">
                            <div className="form-group">
                              <label
                                className="formlable"
                                htmlFor="scope12NumbersType"
                              >
                                Are your emission numbers actual or estimated?
                              </label>
                              <div className="flex flex-wrap gap-3">
                                <div className="flex align-items-center">
                                  <RadioButton
                                    inputId="scope12NumbersActual"
                                    name="scope12NumbersType"
                                    value="Actual"
                                    checked={
                                      formData.scope12NumbersType === "Actual"
                                    }
                                    onChange={(e) =>
                                      handleRadioChange(e, "scope12NumbersType")
                                    }
                                  />
                                  <label
                                    htmlFor="scope12NumbersActual"
                                    className="ml-2 mb-0 formlable"
                                  >
                                    Actual
                                  </label>
                                  <RadioButton
                                    inputId="scope12NumbersEstimated"
                                    name="scope12NumbersType"
                                    value="Estimated"
                                    checked={
                                      formData.scope12NumbersType ===
                                      "Estimated"
                                    }
                                    onChange={(e) =>
                                      handleRadioChange(e, "scope12NumbersType")
                                    }
                                    className="ml-4"
                                  />
                                  <label
                                    htmlFor="scope12NumbersEstimated"
                                    className="ml-2 mb-0 formlable"
                                  >
                                    Estimated
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div className="form-group">
                              <label
                                className="formlable"
                                htmlFor="scope12Verification"
                              >
                                Is your scope 1 and 2 emission numbers
                                externally verified?
                              </label>
                              <div className="flex flex-wrap gap-3">
                                <div className="flex align-items-center">
                                  <RadioButton
                                    inputId="scope12VerificationYes"
                                    name="scope12Verification"
                                    value="true"
                                    checked={
                                      formData.scope12Verification === "true"
                                    }
                                    onChange={(e) =>
                                      handleRadioChange(
                                        e,
                                        "scope12Verification"
                                      )
                                    }
                                  />
                                  <label
                                    htmlFor="scope12VerificationYes"
                                    className="ml-2 mb-0 formlable"
                                  >
                                    Yes
                                  </label>
                                  <RadioButton
                                    inputId="scope12VerificationNo"
                                    name="scope12Verification"
                                    value="false"
                                    checked={
                                      formData.scope12Verification === "false"
                                    }
                                    onChange={(e) =>
                                      handleRadioChange(
                                        e,
                                        "scope12Verification"
                                      )
                                    }
                                    className="ml-4"
                                  />
                                  <label
                                    htmlFor="scope12VerificationNo"
                                    className="ml-2 mb-0 formlable"
                                  >
                                    No
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div className="form-group">
                              <label
                                className="formlable"
                                htmlFor="scope1Emissions"
                              >
                                Scope 1 emissions (tCO2e)
                              </label>
                              <input
                                type="text"
                                id="scope1Emissions"
                                value={formData.scope1Emissions}
                                className={`form-control ${
                                  errors.scope1Emissions ? "is-invalid" : ""
                                }`}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Allow only digits and a single optional decimal point
                                  if (
                                    /^\d+(\.\d{0,})?$/.test(value) ||
                                    value === ""
                                  ) {
                                    setFormData({
                                      ...formData,
                                      scope1Emissions: value,
                                    });
                                  }
                                }}
                                onKeyDown={(e) => {
                                  // Prevent non-numeric inputs
                                  if (
                                    !/[\d.]/.test(e.key) &&
                                    e.key !== "Backspace" &&
                                    e.key !== "Delete" &&
                                    e.key !== "ArrowLeft" &&
                                    e.key !== "ArrowRight"
                                  ) {
                                    e.preventDefault();
                                  }
                                }}
                                placeholder="Enter Scope 1 emissions value"
                              />

                              {errors.scope1Emissions && (
                                <div className="invalid-feedback">
                                  {errors.scope1Emissions}
                                </div>
                              )}
                            </div>
                            <div className="form-group">
                              <label
                                className="formlable"
                                htmlFor="scope2Emissions"
                              >
                                Scope 2 emissions (tCO2e)
                              </label>
                              <input
                                type="text"
                                id="scope2Emissions"
                                value={formData.scope2Emissions}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Allow only digits and a single optional decimal point
                                  if (
                                    /^\d+(\.\d{0,})?$/.test(value) ||
                                    value === ""
                                  ) {
                                    setFormData({
                                      ...formData,
                                      scope2Emissions: value,
                                    });
                                  }
                                }}
                                onKeyDown={(e) => {
                                  // Prevent non-numeric inputs
                                  if (
                                    !/[\d.]/.test(e.key) &&
                                    e.key !== "Backspace" &&
                                    e.key !== "Delete" &&
                                    e.key !== "ArrowLeft" &&
                                    e.key !== "ArrowRight"
                                  ) {
                                    e.preventDefault();
                                  }
                                }}
                                class="form-control"
                                placeholder="Enter Scope 2 emissions value"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="row mt-1">
                          <div className="col-md-5">
                            <div className="form-group">
                              <label
                                className="formlable"
                                htmlFor="scope1Intensity"
                              >
                                Scope 1 emission intensity based on revenue
                              </label>
                              <input
                                type="text"
                                id="scope1Intensity"
                                value={formData.scope1Intensity}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Allow only digits and a single optional decimal point
                                  if (
                                    /^\d+(\.\d{0,})?$/.test(value) ||
                                    value === ""
                                  ) {
                                    setFormData({
                                      ...formData,
                                      scope1Intensity: value,
                                    });
                                  }
                                }}
                                onKeyDown={(e) => {
                                  // Prevent non-numeric inputs
                                  if (
                                    !/[\d.]/.test(e.key) &&
                                    e.key !== "Backspace" &&
                                    e.key !== "Delete" &&
                                    e.key !== "ArrowLeft" &&
                                    e.key !== "ArrowRight"
                                  ) {
                                    e.preventDefault();
                                  }
                                }}
                                class="form-control"
                                placeholder="Enter Scope 1 emissions intensity"
                              />
                            </div>
                          </div>
                          <div className="col-md-5">
                            <div className="row">
                              <div className="col-md-6">
                                <div className="form-group">
                                  <label className="formlable" htmlFor="unit1">
                                    Unit
                                  </label>
                                  <input
                                    type="text"
                                    id="unit1"
                                    value="tCO2e"
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      // Allow only digits and a single optional decimal point
                                      if (
                                        /^\d+(\.\d{0,})?$/.test(value) ||
                                        value === ""
                                      ) {
                                        setFormData({
                                          ...formData,
                                          unit1: value,
                                        });
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      // Prevent non-numeric inputs
                                      if (
                                        !/[\d.]/.test(e.key) &&
                                        e.key !== "Backspace" &&
                                        e.key !== "Delete" &&
                                        e.key !== "ArrowLeft" &&
                                        e.key !== "ArrowRight"
                                      ) {
                                        e.preventDefault();
                                      }
                                    }}
                                    class="form-control"
                                    placeholder="Unit"
                                    readOnly
                                  />
                                </div>
                              </div>

                              <div className="col-md-6">
                                <div className="form-group">
                                  <label
                                    className="formlable"
                                    htmlFor="currency1"
                                  >
                                    Currency
                                  </label>
                                  <Dropdown
                                    id="currency1"
                                    className={`currency form-control wd-100 ${
                                      errors.currency1 ? "is-invalid" : ""
                                    }`}
                                    value={formData.currency1}
                                    onChange={(e) => {
                                      setFormData({
                                        ...formData,
                                        currency1: e.value,
                                      });
                                    }}
                                    options={currency}
                                    placeholder="Select currency"
                                    filter
                                    filterPlaceholder="Search currency"
                                    menuPosition="fixed"
                                  />
                                  {errors.currency1 && (
                                    <div className="invalid-feedback">
                                      {errors.currency1}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row mt-1">
                          <div className="col-md-5">
                            <div className="form-group">
                              <label
                                className="formlable"
                                htmlFor="scope2Intensity"
                              >
                                Scope 2 emission intensity based on revenue
                              </label>
                              <input
                                type="text"
                                id="scope2Intensity"
                                value={formData.scope2Intensity}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Allow only digits and a single optional decimal point
                                  if (
                                    /^\d+(\.\d{0,})?$/.test(value) ||
                                    value === ""
                                  ) {
                                    setFormData({
                                      ...formData,
                                      scope2Intensity: value,
                                    });
                                  }
                                }}
                                onKeyDown={(e) => {
                                  // Prevent non-numeric inputs
                                  if (
                                    !/[\d.]/.test(e.key) &&
                                    e.key !== "Backspace" &&
                                    e.key !== "Delete" &&
                                    e.key !== "ArrowLeft" &&
                                    e.key !== "ArrowRight"
                                  ) {
                                    e.preventDefault();
                                  }
                                }}
                                class="form-control"
                                placeholder="Enter Scope 2 emissions intensity"
                              />
                            </div>
                          </div>
                          <div className="col-md-5">
                            <div className="row">
                              <div className="col-md-6">
                                <div className="form-group">
                                  <label className="formlable" htmlFor="unit2">
                                    Unit
                                  </label>
                                  <input
                                    type="text"
                                    id="unit2"
                                    value="tCO2e"
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      // Allow only digits and a single optional decimal point
                                      if (
                                        /^\d+(\.\d{0,})?$/.test(value) ||
                                        value === ""
                                      ) {
                                        setFormData({
                                          ...formData,
                                          unit2: value,
                                        });
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      // Prevent non-numeric inputs
                                      if (
                                        !/[\d.]/.test(e.key) &&
                                        e.key !== "Backspace" &&
                                        e.key !== "Delete" &&
                                        e.key !== "ArrowLeft" &&
                                        e.key !== "ArrowRight"
                                      ) {
                                        e.preventDefault();
                                      }
                                    }}
                                    class="form-control"
                                    placeholder="Autopopulated"
                                    readOnly
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="form-group">
                                  <label
                                    className="formlable"
                                    htmlFor="currency2"
                                  >
                                    Currency
                                  </label>
                                  <Dropdown
                                    id="currency2"
                                    //className="currency form-control wd-100"
                                    className={`currency form-control wd-100 ${
                                      errors.currency2 ? "is-invalid" : ""
                                    }`}
                                    value={formData.currency2}
                                    onChange={(e) => {
                                      setFormData({
                                        ...formData,
                                        currency2: e.value,
                                      });
                                    }}
                                    options={currency}
                                    placeholder="Select currency"
                                    filter
                                    filterPlaceholder="Search currency"
                                    menuPosition="fixed"
                                  />
                                  {/* <Select
                                    id="currency2"
                                    className="currency"
                                    value={currency.find(
                                      (option) =>
                                        option.value === formData.currency2
                                    )}
                                    onChange={(selectedOption) => {
                                      setFormData({
                                        ...formData,
                                        currency2: selectedOption
                                          ? selectedOption.value
                                          : "", // Update with the selected value or an empty string
                                      });
                                    }}
                                    options={currency}
                                    placeholder="Select currency"
                                    menuPosition="fixed"
                                  /> */}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row mt-1">
                          <div className="col-md-12">
                            <div className="form-group">
                              <label
                                className="formlable"
                                htmlFor="scope12Percentage"
                              >
                                What % of total organizational emissions are
                                covered in this estimation?
                              </label>
                              <div className="row">
                                <div className="col-md-5">
                                  <input
                                    type="text"
                                    id="scope12Percentage"
                                    value={formData.scope12Percentage}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      // Allow only digits and a single optional decimal point
                                      if (
                                        /^\d+(\.\d{0,})?$/.test(value) ||
                                        value === ""
                                      ) {
                                        const capped =
                                          clampOrgEmissionsPercentInput(value);
                                        setFormData({
                                          ...formData,
                                          scope12Percentage: capped,
                                        });
                                        if (errors.scope12Percentage) {
                                          setErrors((prev) => ({
                                            ...prev,
                                            scope12Percentage: "",
                                          }));
                                        }
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      // Prevent non-numeric inputs
                                      if (
                                        !/[\d.]/.test(e.key) &&
                                        e.key !== "Backspace" &&
                                        e.key !== "Delete" &&
                                        e.key !== "ArrowLeft" &&
                                        e.key !== "ArrowRight"
                                      ) {
                                        e.preventDefault();
                                      }
                                    }}
                                    className={`form-control ${
                                      errors.scope12Percentage
                                        ? "is-invalid"
                                        : ""
                                    }`}
                                    placeholder="Enter % of total organizational emissions"
                                    inputMode="decimal"
                                  />
                                  {errors.scope12Percentage && (
                                    <div className="invalid-feedback d-block">
                                      {errors.scope12Percentage}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </TabPanel>
                  <TabPanel header="Scope 3">
                    <div className="row gy-4">
                      <div className="col col-md-12">
                        <label
                          className="formlable"
                          htmlFor="scope3Calculation"
                        >
                          Do you calculate your company's scope 3 upstream and
                          waste emissions? <span className="mark-form">*</span>
                        </label>
                        <div className="flex flex-wrap gap-3">
                          <div className="flex align-items-center">
                            <RadioButton
                              inputId="scope3Yes"
                              name="scope3Calculation"
                              value="true"
                              checked={formData.scope3Calculation === "true"}
                              onChange={(e) =>
                                handleRadioChange(e, "scope3Calculation")
                              }
                            />
                            <label
                              htmlFor="scope3Yes"
                              className="ml-2 mb-0 formlable"
                            >
                              Yes
                            </label>
                            <RadioButton
                              inputId="scope3No"
                              name="scope3Calculation"
                              value="false"
                              checked={formData.scope3Calculation === "false"}
                              onChange={(e) =>
                                handleRadioChange(e, "scope3Calculation")
                              }
                              className="ml-4"
                            />
                            <label
                              htmlFor="scope3No"
                              className="ml-2 mb-0 formlable"
                            >
                              No
                            </label>
                          </div>
                        </div>
                        {errors.scope3Calculation && (
                          <p className="text-danger">
                            {errors.scope3Calculation}
                          </p>
                        )}
                      </div>
                    </div>
                    {formData.scope3Calculation === "true" && (
                      <div className="scope3">
                        <div className="row mt-4">
                          <div className="col-md-5">
                            <div className="form-group">
                              <label
                                className="formlable"
                                htmlFor="scope3NumbersType"
                              >
                                Are your emission numbers actual or estimated?
                              </label>
                              <div className="flex flex-wrap gap-3">
                                <div className="flex align-items-center">
                                  <RadioButton
                                    inputId="scope3NumbersActual"
                                    name="scope3NumbersType"
                                    value="Actual"
                                    checked={
                                      formData.scope3NumbersType === "Actual"
                                    }
                                    onChange={(e) =>
                                      handleRadioChange(e, "scope3NumbersType")
                                    }
                                  />
                                  <label
                                    htmlFor="scope3NumbersActual"
                                    className="ml-2 mb-0 formlable"
                                  >
                                    Actual
                                  </label>
                                  <RadioButton
                                    inputId="scope3NumbersEstimated"
                                    name="scope3NumbersType"
                                    value="Estimated"
                                    checked={
                                      formData.scope3NumbersType === "Estimated"
                                    }
                                    onChange={(e) =>
                                      handleRadioChange(e, "scope3NumbersType")
                                    }
                                    className="ml-4"
                                  />
                                  <label
                                    htmlFor="scope3NumbersEstimated"
                                    className="ml-2 mb-0 formlable"
                                  >
                                    Estimated
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div className="form-group">
                              <label
                                className="formlable"
                                htmlFor="scope3Verification"
                              >
                                Is your scope 3 upstream and waste emission
                                numbers externally verified?
                              </label>
                              <div className="flex flex-wrap gap-3">
                                <div className="flex align-items-center">
                                  <RadioButton
                                    inputId="scope3VerificationYes"
                                    name="scope3Verification"
                                    value="true"
                                    checked={
                                      formData.scope3Verification === "true"
                                    }
                                    onChange={(e) =>
                                      handleRadioChange(e, "scope3Verification")
                                    }
                                  />
                                  <label
                                    htmlFor="scope3VerificationYes"
                                    className="ml-2 mb-0 formlable"
                                  >
                                    Yes
                                  </label>
                                  <RadioButton
                                    inputId="scope3VerificationNo"
                                    name="scope3Verification"
                                    value="false"
                                    checked={
                                      formData.scope3Verification === "false"
                                    }
                                    onChange={(e) =>
                                      handleRadioChange(e, "scope3Verification")
                                    }
                                    className="ml-4"
                                  />
                                  <label
                                    htmlFor="scope3VerificationNo"
                                    className="ml-2 mb-0 formlable"
                                  >
                                    No
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div className="form-group">
                              <label
                                className="formlable"
                                htmlFor="upstreamEmissions"
                              >
                                Scope 3 upstream emissions (tCO2e)
                              </label>
                              <input
                                type="text"
                                id="upstreamEmissions"
                                value={formData.upstreamEmissions}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Allow only digits and a single optional decimal point
                                  if (
                                    /^\d+(\.\d{0,})?$/.test(value) ||
                                    value === ""
                                  ) {
                                    setFormData({
                                      ...formData,
                                      upstreamEmissions: value,
                                    });
                                  }
                                }}
                                onKeyDown={(e) => {
                                  // Prevent non-numeric inputs
                                  if (
                                    !/[\d.]/.test(e.key) &&
                                    e.key !== "Backspace" &&
                                    e.key !== "Delete" &&
                                    e.key !== "ArrowLeft" &&
                                    e.key !== "ArrowRight"
                                  ) {
                                    e.preventDefault();
                                  }
                                }}
                                className="form-control"
                                placeholder="Enter Scope 3 upstream emissions value"
                              />

                              {errors.upstreamEmissions && (
                                <div className="invalid-feedback">
                                  {errors.upstreamEmissions}
                                </div>
                              )}
                            </div>
                            <div className="form-group">
                              <label
                                className="formlable"
                                htmlFor="wasteEmissions"
                              >
                                Scope 3 waste emissions (tCO2e)
                              </label>
                              <input
                                type="text"
                                id="wasteEmissions"
                                value={formData.wasteEmissions}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Allow only digits and a single optional decimal point
                                  if (
                                    /^\d+(\.\d{0,})?$/.test(value) ||
                                    value === ""
                                  ) {
                                    setFormData({
                                      ...formData,
                                      wasteEmissions: value,
                                    });
                                  }
                                }}
                                onKeyDown={(e) => {
                                  // Prevent non-numeric inputs
                                  if (
                                    !/[\d.]/.test(e.key) &&
                                    e.key !== "Backspace" &&
                                    e.key !== "Delete" &&
                                    e.key !== "ArrowLeft" &&
                                    e.key !== "ArrowRight"
                                  ) {
                                    e.preventDefault();
                                  }
                                }}
                                className="form-control"
                                placeholder="Enter Scope 3 waste emissions value"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="row mt-1">
                          <div className="col-md-5">
                            <div className="form-group">
                              <label
                                className="formlable"
                                htmlFor="upstreamIntensity"
                              >
                                Upstream emission intensity based on revenue
                              </label>
                              <input
                                type="text"
                                id="upstreamIntensity"
                                value={formData.upstreamIntensity}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Allow only digits and a single optional decimal point
                                  if (
                                    /^\d+(\.\d{0,})?$/.test(value) ||
                                    value === ""
                                  ) {
                                    setFormData({
                                      ...formData,
                                      upstreamIntensity: value,
                                    });
                                  }
                                }}
                                onKeyDown={(e) => {
                                  // Prevent non-numeric inputs
                                  if (
                                    !/[\d.]/.test(e.key) &&
                                    e.key !== "Backspace" &&
                                    e.key !== "Delete" &&
                                    e.key !== "ArrowLeft" &&
                                    e.key !== "ArrowRight"
                                  ) {
                                    e.preventDefault();
                                  }
                                }}
                                className="form-control"
                                placeholder="Enter Scope 3 upstream emissions intensity"
                              />
                            </div>
                          </div>
                          <div className="col-md-5">
                            <div className="row">
                              <div className="col-md-6">
                                <div className="form-group">
                                  <label className="formlable" htmlFor="unit3">
                                    Unit
                                  </label>
                                  <input
                                    type="text"
                                    id="unit3"
                                    value="tCO2e"
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      // Allow only digits and a single optional decimal point
                                      if (
                                        /^\d+(\.\d{0,})?$/.test(value) ||
                                        value === ""
                                      ) {
                                        setFormData({
                                          ...formData,
                                          unit3: value,
                                        });
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      // Prevent non-numeric inputs
                                      if (
                                        !/[\d.]/.test(e.key) &&
                                        e.key !== "Backspace" &&
                                        e.key !== "Delete" &&
                                        e.key !== "ArrowLeft" &&
                                        e.key !== "ArrowRight"
                                      ) {
                                        e.preventDefault();
                                      }
                                    }}
                                    class="form-control"
                                    placeholder="Unit"
                                    readOnly
                                    aria-readonly="true"
                                  />
                                </div>
                              </div>

                              <div className="col-md-6">
                                <div className="form-group">
                                  <label
                                    className="formlable"
                                    htmlFor="currency3"
                                  >
                                    Currency
                                  </label>
                                  <Dropdown
                                    id="currency3"
                                    className={`currency form-control wd-100 ${
                                      errors.currency3 ? "is-invalid" : ""
                                    }`}
                                    value={formData.currency3}
                                    onChange={(e) => {
                                      setFormData({
                                        ...formData,
                                        currency3: e.value,
                                      });
                                    }}
                                    options={currency}
                                    placeholder="Select currency"
                                    filter
                                    filterPlaceholder="Search currency"
                                    menuPosition="fixed"
                                  />
                                  {errors.currency3 && (
                                    <div className="invalid-feedback">
                                      {errors.currency3}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row mt-1">
                          <div className="col-md-5">
                            <div className="form-group">
                              <label
                                className="formlable"
                                htmlFor="waterIntensity"
                              >
                                Waste emission intensity based on revenue
                              </label>
                              <input
                                type="text"
                                id="waterIntensity"
                                value={formData.waterIntensity}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Allow only digits and a single optional decimal point
                                  if (
                                    /^\d+(\.\d{0,})?$/.test(value) ||
                                    value === ""
                                  ) {
                                    setFormData({
                                      ...formData,
                                      waterIntensity: value,
                                    });
                                  }
                                }}
                                onKeyDown={(e) => {
                                  // Prevent non-numeric inputs
                                  if (
                                    !/[\d.]/.test(e.key) &&
                                    e.key !== "Backspace" &&
                                    e.key !== "Delete" &&
                                    e.key !== "ArrowLeft" &&
                                    e.key !== "ArrowRight"
                                  ) {
                                    e.preventDefault();
                                  }
                                }}
                                class="form-control"
                                placeholder="Enter Scope 3 waste emissions intensity"
                              />
                            </div>
                          </div>
                          <div className="col-md-5">
                            <div className="row">
                              <div className="col-md-6">
                                <div className="form-group">
                                  <label className="formlable" htmlFor="unit4">
                                    Unit
                                  </label>
                                  <input
                                    type="text"
                                    id="unit4"
                                    value="tCO2e"
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      // Allow only digits and a single optional decimal point
                                      if (
                                        /^\d+(\.\d{0,})?$/.test(value) ||
                                        value === ""
                                      ) {
                                        setFormData({
                                          ...formData,
                                          unit4: value,
                                        });
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      // Prevent non-numeric inputs
                                      if (
                                        !/[\d.]/.test(e.key) &&
                                        e.key !== "Backspace" &&
                                        e.key !== "Delete" &&
                                        e.key !== "ArrowLeft" &&
                                        e.key !== "ArrowRight"
                                      ) {
                                        e.preventDefault();
                                      }
                                    }}
                                    class="form-control"
                                    placeholder="Unit"
                                    readOnly
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="form-group">
                                  <label
                                    className="formlable"
                                    htmlFor="currency4"
                                  >
                                    Currency
                                  </label>
                                  <Dropdown
                                    id="currency4"
                                    className={`currency form-control wd-100 ${
                                      errors.currency4 ? "is-invalid" : ""
                                    }`}
                                    value={formData.currency4}
                                    onChange={(e) => {
                                      setFormData({
                                        ...formData,
                                        currency4: e.value,
                                      });
                                    }}
                                    options={currency}
                                    placeholder="Select currency"
                                    filter
                                    filterPlaceholder="Search currency"
                                    menuPosition="fixed"
                                  />
                                  {errors.currency4 && (
                                    <div className="invalid-feedback">
                                      {errors.currency4}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row mt-1">
                          <div className="col-md-12">
                            <div className="form-group">
                              <label
                                className="formlable"
                                htmlFor="scope3Percentage"
                              >
                                What % of total organizational emissions are
                                covered in this estimation?
                              </label>
                              <div className="row">
                                <div className="col-md-5">
                                  <input
                                    type="text"
                                    id="scope3Percentage"
                                    value={formData.scope3Percentage}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      // Allow only digits and a single optional decimal point
                                      if (
                                        /^\d+(\.\d{0,})?$/.test(value) ||
                                        value === ""
                                      ) {
                                        const capped =
                                          clampOrgEmissionsPercentInput(value);
                                        setFormData({
                                          ...formData,
                                          scope3Percentage: capped,
                                        });
                                        if (errors.scope3Percentage) {
                                          setErrors((prev) => ({
                                            ...prev,
                                            scope3Percentage: "",
                                          }));
                                        }
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      // Prevent non-numeric inputs
                                      if (
                                        !/[\d.]/.test(e.key) &&
                                        e.key !== "Backspace" &&
                                        e.key !== "Delete" &&
                                        e.key !== "ArrowLeft" &&
                                        e.key !== "ArrowRight"
                                      ) {
                                        e.preventDefault();
                                      }
                                    }}
                                    className={`form-control ${
                                      errors.scope3Percentage
                                        ? "is-invalid"
                                        : ""
                                    }`}
                                    placeholder="Enter % of total organizational emissions"
                                    inputMode="decimal"
                                  />
                                  {errors.scope3Percentage && (
                                    <div className="invalid-feedback">
                                      {errors.scope3Percentage}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </TabPanel>
                </TabView>
              </div>
            </form>
            <div className="forms-buttons-container">
              <Button
                className="outline-btn"
                variant="contained"
                color="primary"
                startIcon={
                  <ArrowBackIosIcon
                    sx={{ fontSize: "12px!important", marginRight: "0" }}
                  />
                }
                onClick={() => navigate("/energy-emissions")}
              >
                Back
              </Button>
              <Button
                className="next-button"
                variant="contained"
                color="primary"
                endIcon={
                  <ArrowForwardIosIcon
                    sx={{ fontSize: "12px!important", marginLeft: "0" }}
                  />
                }
                onClick={handleNext}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SCFScope;
